import React, { useState } from "react";
import { FiCalendar, FiPackage, FiDownload } from "react-icons/fi";
import { reporteIngresosProductos } from "../api/reportes";
import { showAlertExito, showAlertError, showAlertInfo } from "../utils/alertHelper";
import { formatDate } from "../utils/fechas";
import Loader from "./Loader";

const ReporteIngresosProductos = () => {
    const [loading, setLoading] = useState(false);
    const [reportData, setReportData] = useState([]);

    // Paginación
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage] = useState(10);

    // Fechas por defecto
    const todayDate = new Date();
    const firstDay = formatDate(new Date(todayDate.getFullYear(), todayDate.getMonth(), 1));
    const lastDay = formatDate(todayDate);

    const [fechaInicio, setFechaInicio] = useState(firstDay);
    const [fechaFin, setFechaFin] = useState(lastDay);

    const handleGenerarReporte = async (e) => {
        e.preventDefault();
        setLoading(true);
        setReportData([]);
        setCurrentPage(1);
        try {
            const payload = { fechaInicio, fechaFin: `${fechaFin} 23:59:59` };
            const response = await reporteIngresosProductos(payload);

            if (response && response.data && response.data.length > 0) {
                setReportData(response.data);
                showAlertExito("Éxito", "Reporte generado correctamente.");
            } else {
                showAlertInfo("Sin datos", "No se encontraron ingresos para el periodo seleccionado.");
            }
        } catch (error) {
            console.error("Error generando reporte:", error);
        } finally {
            setLoading(false);
        }
    };

    // Exportar a CSV
    const exportToExcel = () => {
        if (reportData.length === 0) return;

        const headers = ["ID Producto", "Nombre", "SKU", "Categoría", "Total Cantidad"];
        const rows = reportData.map(item => [
            item.id_producto,
            item.Producto?.name || "---",
            item.Producto?.sku || "---",
            item.Producto?.Categoria?.name || "---",
            item.total_cantidad
        ]);

        const csvContent = "data:text/csv;charset=utf-8,\uFEFF"
            + headers.join(",") + "\n"
            + rows.map(e => e.join(",")).join("\n");

        const encodedUri = encodeURI(csvContent);
        const link = document.createElement("a");
        link.setAttribute("href", encodedUri);
        link.setAttribute("download", `Reporte_Ingresos_Productos_${fechaInicio}_al_${fechaFin}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    // Paginación
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentItems = reportData.slice(indexOfFirstItem, indexOfLastItem);
    const totalPages = Math.ceil(reportData.length / itemsPerPage);

    const paginate = (pageNumber) => setCurrentPage(pageNumber);

    // Total general
    const totalGeneral = reportData.reduce((sum, item) => sum + Number(item.total_cantidad || 0), 0);

    return (
        <div className="p-6 max-w-6xl mx-auto">
            <div className="mb-8">
                <div className="flex justify-between items-start">
                    <div>
                        <h2 className="text-2xl font-bold text-slate-800 flex items-center gap-3">
                            <FiPackage className="text-indigo-600" /> Reporte de Ingresos de Productos
                        </h2>
                        <p className="text-slate-500 mt-1">
                            Seleccione el rango de fechas para ver el total de productos ingresados.
                        </p>
                    </div>
                    {reportData.length > 0 && (
                        <button
                            onClick={exportToExcel}
                            className="flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white px-4 py-2 rounded-lg font-semibold transition-all shadow-md active:scale-95"
                        >
                            <FiDownload /> Exportar a Excel
                        </button>
                    )}
                </div>
            </div>

            <div className="bg-white rounded-2xl shadow-sm border border-slate-200 relative">
                {loading && (
                    <div className="absolute inset-0 bg-white/70 flex justify-center items-center z-10 rounded-2xl">
                        <Loader />
                    </div>
                )}

                <form onSubmit={handleGenerarReporte} className="p-8 space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {/* Fecha Inicio */}
                        <div className="space-y-2">
                            <label className="block text-sm font-semibold text-slate-700">
                                Fecha de Inicio
                            </label>
                            <div className="relative">
                                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                    <FiCalendar className="text-slate-400" />
                                </div>
                                <input
                                    type="date"
                                    value={fechaInicio}
                                    onChange={(e) => setFechaInicio(e.target.value)}
                                    className="block w-full pl-10 pr-4 py-2.5 border border-slate-300 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all bg-slate-50 focus:bg-white text-slate-700"
                                    required
                                />
                            </div>
                        </div>

                        {/* Fecha Fin */}
                        <div className="space-y-2">
                            <label className="block text-sm font-semibold text-slate-700">
                                Fecha de Fin
                            </label>
                            <div className="relative">
                                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                    <FiCalendar className="text-slate-400" />
                                </div>
                                <input
                                    type="date"
                                    value={fechaFin}
                                    onChange={(e) => setFechaFin(e.target.value)}
                                    className="block w-full pl-10 pr-4 py-2.5 border border-slate-300 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all bg-slate-50 focus:bg-white text-slate-700"
                                    required
                                />
                            </div>
                        </div>
                    </div>

                    <div className="pt-4 flex justify-end">
                        <button
                            type="submit"
                            disabled={loading}
                            className={`flex items-center gap-2 px-6 py-3 rounded-xl font-bold text-white transition-all shadow-lg ${loading
                                ? 'bg-indigo-400 cursor-not-allowed'
                                : 'bg-indigo-600 hover:bg-indigo-700 shadow-indigo-200 active:scale-95'
                                }`}
                        >
                            <FiPackage /> {loading ? "Generando..." : "Generar Reporte"}
                        </button>
                    </div>
                </form>
            </div>

            {/* Tabla de Resultados */}
            {reportData.length > 0 && (
                <div className="mt-8 bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden animate-fade-in">
                    <div className="p-6 border-b border-slate-100 flex justify-between items-center bg-slate-50/50">
                        <h3 className="font-bold text-slate-800 flex items-center gap-2">
                            <FiPackage className="text-indigo-600" /> Total de Ingresos por Producto
                        </h3>
                        <div className="flex items-center gap-3">
                            <span className="text-xs font-semibold px-3 py-1 bg-indigo-100 text-indigo-700 rounded-full">
                                {reportData.length} productos
                            </span>
                            <span className="text-xs font-semibold px-3 py-1 bg-emerald-100 text-emerald-700 rounded-full">
                                Total: {totalGeneral.toLocaleString()} uds.
                            </span>
                        </div>
                    </div>
                    <div className="overflow-x-auto custom-scrollbar">
                        <table className="w-full text-sm text-left">
                            <thead className="text-xs text-slate-500 bg-slate-50 uppercase border-b border-slate-200">
                                <tr>
                                    <th className="px-6 py-4 font-semibold">#</th>
                                    <th className="px-6 py-4 font-semibold">ID Producto</th>
                                    <th className="px-6 py-4 font-semibold">Nombre</th>
                                    <th className="px-6 py-4 font-semibold">SKU</th>
                                    <th className="px-6 py-4 font-semibold">Categoría</th>
                                    <th className="px-6 py-4 font-semibold text-right">Total Cantidad</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100">
                                {currentItems.map((item, idx) => (
                                    <tr key={item.id_producto} className="hover:bg-indigo-50/30 transition-colors group">
                                        <td className="px-6 py-4 text-slate-400 text-xs">
                                            {indexOfFirstItem + idx + 1}
                                        </td>
                                        <td className="px-6 py-4 text-slate-500 font-mono text-xs">
                                            {item.id_producto}
                                        </td>
                                        <td className="px-6 py-4 text-slate-800 font-semibold">
                                            {item.Producto?.name || "---"}
                                        </td>
                                        <td className="px-6 py-4">
                                            <span className="px-2 py-0.5 bg-slate-100 text-slate-600 rounded text-xs font-mono">
                                                {item.Producto?.sku || "---"}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4">
                                            {item.Producto?.Categoria?.name
                                                ? <span className="px-2 py-0.5 bg-purple-50 text-purple-700 border border-purple-100 rounded-full text-xs font-medium">
                                                    {item.Producto.Categoria.name}
                                                  </span>
                                                : <span className="text-slate-400 text-xs">---</span>
                                            }
                                        </td>
                                        <td className="px-6 py-4 text-right">
                                            <span className="text-indigo-700 font-bold text-base">
                                                {Number(item.total_cantidad).toLocaleString()}
                                            </span>
                                            <span className="text-slate-400 text-xs ml-1">uds.</span>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                            <tfoot className="border-t-2 border-slate-200 bg-slate-50/80">
                                <tr>
                                    <td colSpan={5} className="px-6 py-3 text-sm font-bold text-slate-700 text-right">
                                        Total General:
                                    </td>
                                    <td className="px-6 py-3 text-right">
                                        <span className="text-emerald-700 font-extrabold text-base">
                                            {totalGeneral.toLocaleString()}
                                        </span>
                                        <span className="text-slate-400 text-xs ml-1">uds.</span>
                                    </td>
                                </tr>
                            </tfoot>
                        </table>
                    </div>

                    {/* Controles de Paginación */}
                    {totalPages > 1 && (
                        <div className="p-4 border-t border-slate-100 bg-slate-50/30 flex items-center justify-between">
                            <p className="text-xs text-slate-500">
                                Mostrando {indexOfFirstItem + 1} a {Math.min(indexOfLastItem, reportData.length)} de {reportData.length} registros
                            </p>
                            <div className="flex gap-2">
                                <button
                                    onClick={() => paginate(currentPage - 1)}
                                    disabled={currentPage === 1}
                                    className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${currentPage === 1
                                        ? 'text-slate-300 cursor-not-allowed'
                                        : 'text-slate-600 hover:bg-white hover:shadow-sm border border-slate-200'
                                        }`}
                                >
                                    Anterior
                                </button>
                                {[...Array(totalPages)].map((_, i) => {
                                    const pageNum = i + 1;
                                    if (totalPages <= 7 ||
                                        pageNum === 1 ||
                                        pageNum === totalPages ||
                                        (pageNum >= currentPage - 1 && pageNum <= currentPage + 1)) {
                                        return (
                                            <button
                                                key={i}
                                                onClick={() => paginate(pageNum)}
                                                className={`w-8 h-8 rounded-lg text-sm font-bold transition-all ${currentPage === pageNum
                                                    ? 'bg-indigo-600 text-white shadow-indigo-100 shadow-md'
                                                    : 'text-slate-600 hover:bg-white border border-transparent hover:border-slate-200'
                                                    }`}
                                            >
                                                {pageNum}
                                            </button>
                                        );
                                    } else if (pageNum === currentPage - 2 || pageNum === currentPage + 2) {
                                        return <span key={i} className="px-1 text-slate-400">...</span>;
                                    }
                                    return null;
                                })}
                                <button
                                    onClick={() => paginate(currentPage + 1)}
                                    disabled={currentPage === totalPages}
                                    className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${currentPage === totalPages
                                        ? 'text-slate-300 cursor-not-allowed'
                                        : 'text-slate-600 hover:bg-white hover:shadow-sm border border-slate-200'
                                        }`}
                                >
                                    Siguiente
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            )}

            {/* Info */}
            <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-blue-50 border border-blue-100 p-4 rounded-xl flex gap-3">
                    <div className="bg-blue-100 p-2 rounded-lg h-fit text-blue-600">
                        <FiPackage size={20} />
                    </div>
                    <div>
                        <h4 className="font-bold text-blue-800 text-sm">Ingresos Totales</h4>
                        <p className="text-blue-600 text-xs mt-1">
                            Este reporte muestra la cantidad total de unidades ingresadas por producto en el periodo seleccionado.
                        </p>
                    </div>
                </div>
                <div className="bg-indigo-50 border border-indigo-100 p-4 rounded-xl flex gap-3">
                    <div className="bg-indigo-100 p-2 rounded-lg h-fit text-indigo-600">
                        <FiCalendar size={20} />
                    </div>
                    <div>
                        <h4 className="font-bold text-indigo-800 text-sm">Periodo de Tiempo</h4>
                        <p className="text-indigo-600 text-xs mt-1">
                            El sistema toma por defecto el primer día del mes actual (según la zona horaria de El Salvador) para facilitar la generación mensual.
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ReporteIngresosProductos;
